import React, { useCallback, useMemo } from 'react';
import { cssMap } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { useCancelledAnalytics } from '../analytics.ts';
import { useSuggestionsLoadingMessages } from '../useRotatingLoadingMessage.ts';

const styles = cssMap({
	container: {
		// Sticks above the footer once the list overflows; the panel's `gap` spaces it from the last card.
		position: 'sticky',
		// Offset is inside the layout's 80px bottom padding; -6px puts the bar 14px above Discard all.
		insetBlockEnd: token('space.negative.075'),
		// Above the footer (`zIndex: 1`); otherwise the Discard all container's fade covers the bar's bottom shadow.
		zIndex: 2,
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		paddingInline: token('space.200'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.rovo.surface.overlay'),
		boxShadow: token('elevation.shadow.overflow'),
	},

	barRow: {
		width: '100%',
		paddingBlock: token('space.150'),
	},

	placeholderAvatarSlot: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '24px',
		height: '24px',
	},
});

/** The agent doing the review, as exposed by the AI suggestions plugin state. */
export type SuggestionsInlineLoadingBarAgent = {
	id: string;
	identityAccountId?: string | null;
	name: string;
	namedId?: string;
};

type SuggestionsInlineLoadingBarProps = {
	/**
	 * The agent doing the review. It is named in the first loading message, and its
	 * avatar is shown alongside that message (then the bar switches to the usual
	 * loading shape), matching the editor's thinking bar. Falls back to the generic
	 * Rovo copy and loading shape when absent.
	 */
	agent?: SuggestionsInlineLoadingBarAgent;
	api?: FullPageEditorPresetAPI;
	/**
	 * `true` while the reviewing agent is still loading. The bar then holds on "Preparing
	 * agent" with a placeholder agent avatar (no rotation) until `agent` resolves.
	 */
	isAgentLoading?: boolean;
	/**
	 * Epoch ms the message rotation is anchored to (from shared plugin state): when the
	 * reviewing agent became ready, falling back to when the thinking session started.
	 * Passed through so this bar stays in sync with the editor's thinking bar.
	 */
	startedAt?: number | null;
};

/**
 * Compact loading bar shown at the bottom of the suggestions list while Rovo
 * regenerates with existing suggestions still visible. Reuses the editor's
 * `RovoThinkingBar` so it stays in sync with the full-page loading bar.
 * Sticks above the footer when the list scrolls, so it is always visible.
 */
export const SuggestionsInlineLoadingBar = ({
	agent,
	api,
	isAgentLoading = false,
	startedAt,
}: SuggestionsInlineLoadingBarProps): React.JSX.Element => {
	// No scroll-into-view: the sticky bar is always visible.
	const rotatingMessages = useSuggestionsLoadingMessages({
		agentName: agent?.name,
		isAgentLoading,
	});
	const fireCancelledAnalytics = useCancelledAnalytics(api?.analytics?.actions.fireAnalyticsEvent);

	const handleStop = useCallback(() => {
		fireCancelledAnalytics();
		api?.aiSuggestions?.actions?.setThinkingState('idle');
	}, [api, fireCancelledAnalytics]);

	const agentId = agent?.id;
	const agentNamedId = agent?.namedId;
	const agentIdentityAccountId = agent?.identityAccountId;
	const firstMessage = useMemo(() => {
		if (agentId) {
			return {
				icon: (
					<AgentAvatar
						agentId={agentId}
						agentNamedId={agentNamedId}
						agentIdentityAccountId={agentIdentityAccountId}
						size="small"
						showBorder={false}
					/>
				),
			};
		}
		if (isAgentLoading) {
			return {
				icon: (
					<span css={styles.placeholderAvatarSlot}>
						<Avatar
							appearance="hexagon"
							size="small"
							borderColor="transparent"
							testId="suggestions-panel-preparing-agent-avatar"
						/>
					</span>
				),
			};
		}
		return undefined;
	}, [agentId, agentNamedId, agentIdentityAccountId, isAgentLoading]);

	return (
		<div css={styles.container} data-testid="suggestions-panel-inline-loading-bar">
			<div css={styles.card} data-testid="suggestions-panel-inline-loading-bar-card">
				<div css={styles.barRow}>
					<RovoThinkingBar
						onStop={handleStop}
						useRotatingTitle
						customRotatingMessages={rotatingMessages}
						startedAt={startedAt}
						firstMessage={firstMessage}
					/>
				</div>
			</div>
		</div>
	);
};
